import type { PresenceFix } from '@cht/shared'
import type { GeoFix } from '../platform/geolocation.js'
import type { FieldClient } from '../api/field-client.js'

export type PresenceConnection = 'connecting' | 'live' | 'reconnecting'
export type PresenceSnapshotStatus = 'loading' | 'ready' | 'unavailable'
export interface PresenceSnapshot { people: PresenceFix[]; connection: PresenceConnection; snapshotStatus: PresenceSnapshotStatus }
type Listener = (snapshot: PresenceSnapshot) => void

export interface PresenceSocket {
  readonly readyState: number
  onopen: (() => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onclose: (() => void) | null
  send(data: string): void
  close(): void
}
type SocketFactory = (url: string) => PresenceSocket

const defaultSocketFactory: SocketFactory = (url) => new WebSocket(url) as unknown as PresenceSocket

/** Cold-start snapshot plus live property-scoped updates. */
export class PresenceClient {
  private readonly listeners = new Set<Listener>()
  private readonly people = new Map<string, PresenceFix>()
  private connection: PresenceConnection = 'connecting'
  private snapshotStatus: PresenceSnapshotStatus = 'loading'
  private socket: PresenceSocket | undefined
  private stopped = true
  private retryTimer: ReturnType<typeof setTimeout> | undefined

  constructor(
    private readonly propertyId: string,
    private readonly api: Pick<FieldClient, 'presence'>,
    private readonly socketFactory: SocketFactory = defaultSocketFactory
  ) {}

  snapshot(): PresenceSnapshot {
    return { people: [...this.people.values()].sort((a, b) => b.recordedAt - a.recordedAt), connection: this.connection, snapshotStatus: this.snapshotStatus }
  }
  subscribe(listener: Listener): () => void { this.listeners.add(listener); listener(this.snapshot()); return () => this.listeners.delete(listener) }

  async start(): Promise<void> {
    this.stopped = false
    try {
      for (const person of await this.api.presence()) this.upsert(person)
      this.snapshotStatus = 'ready'
    } catch { this.connection = 'reconnecting'; this.snapshotStatus = 'unavailable' }
    this.emit()
    this.connect()
  }

  stop(): void {
    this.stopped = true
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = undefined
    this.socket?.close()
    this.socket = undefined
  }

  send(fix: GeoFix): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return
    this.socket.send(JSON.stringify({
      latitude: fix.latitude, longitude: fix.longitude,
      accuracyMeters: fix.accuracyMeters, recordedAt: fix.recordedAt
    }))
  }

  private connect(): void {
    if (this.stopped) return
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    let socket: PresenceSocket
    try { socket = this.socketFactory(`${protocol}//${location.host}/api/properties/${encodeURIComponent(this.propertyId)}/presence`) }
    catch {
      this.connection = 'reconnecting'; this.emit()
      this.retryTimer = setTimeout(() => this.connect(), 5_000)
      return
    }
    this.socket = socket
    socket.onopen = () => { this.connection = 'live'; this.emit() }
    socket.onmessage = (event) => {
      try {
        const person = JSON.parse(String(event.data)) as PresenceFix
        if (person.propertyId === this.propertyId && typeof person.userId === 'string' && typeof person.displayName === 'string'
          && Number.isFinite(person.latitude) && Number.isFinite(person.longitude)
          && Number.isFinite(person.accuracyMeters) && Number.isFinite(person.recordedAt)) {
          this.upsert(person); this.emit()
        }
      } catch { /* Ignore malformed peer frames. */ }
    }
    socket.onclose = () => {
      if (this.stopped) return
      this.connection = 'reconnecting'; this.emit()
      this.retryTimer = setTimeout(() => this.connect(), 5_000)
    }
  }

  private upsert(person: PresenceFix): void {
    const current = this.people.get(person.userId)
    if (!current || person.recordedAt >= current.recordedAt) this.people.set(person.userId, person)
  }
  private emit(): void { const snapshot = this.snapshot(); for (const listener of this.listeners) listener(snapshot) }
}
